// Design frames: wireframes/14-today-by-role-m3.html (14.9), hifi dark lane follows.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, List, ListItem, Notice, Segmented, Skeleton, StatTile } from '@sauda/ui/kit';
import { Screen, useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { formatRupees } from '../lib/money.js';
import { istDateInput } from '../lib/money-input.js';
import { formatCalendarDate } from '../lib/time.js';
import type { BrokerageRow } from '../lib/types.js';
import { ReceiptSheet } from './shared/ReceiptSheet.js';

type Bucket = 'overdue' | 'due' | 'received';
interface Row extends BrokerageRow {
  agentName?: string | null;
  branchName?: string;
  dealStartedOn?: string;
}

/** Which bucket a deal's brokerage is in: paid in full, past its date, or still to come. */
export function bucketOf(row: Pick<BrokerageRow, 'balancePaise' | 'dueOn'>, today: string): Bucket {
  if (row.balancePaise <= 0) return 'received';
  return row.dueOn !== null && row.dueOn < today ? 'overdue' : 'due';
}

/** Money, for accounts: what is overdue, due and received, and a way to record a receipt. No client contact. */
export function MoneyScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const [bucket, setBucket] = useState<Bucket>('overdue');
  const [selected, setSelected] = useState<Row | null>(null);
  const rows = useQuery({
    queryKey: ['brokerage', auth.agencyId],
    queryFn: () => api<Row[]>('/brokerage'),
  });
  const today = istDateInput();
  const all = rows.data ?? [];
  const sum = (b: Bucket, pick: (r: Row) => number) =>
    all.filter((r) => bucketOf(r, today) === b).reduce((s, r) => s + pick(r), 0);
  const shown = all.filter((r) => bucketOf(r, today) === bucket);
  return (
    <Screen
      title={t('nav.money')}
      large
      eyebrow={`${auth.current?.agencyName ?? ''} · ${t('today.allBranches')}`}
    >
      {rows.isPending ? <Skeleton height={120} /> : null}
      {rows.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void rows.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('today.loadFailed')}
        </Notice>
      ) : null}
      {rows.isSuccess ? (
        <>
          <p className="k-sr-only" data-testid="money-due">
            {formatRupees(
              sum('overdue', (r) => r.balancePaise) + sum('due', (r) => r.balancePaise),
            )}
          </p>
          <div className="k-stat-row">
            <StatTile
              compact
              label={t('money.overdue')}
              value={formatRupees(sum('overdue', (r) => r.balancePaise))}
            />
            <StatTile
              compact
              label={t('money.dueTab')}
              value={formatRupees(sum('due', (r) => r.balancePaise))}
            />
            <StatTile
              compact
              label={t('money.received')}
              value={formatRupees(
                sum('received', (r) => r.receivedPaise) +
                  sum('overdue', (r) => r.receivedPaise) +
                  sum('due', (r) => r.receivedPaise),
              )}
            />
          </div>
          <Segmented
            block
            label={t('money.filter')}
            value={bucket}
            onChange={(v) => setBucket(v as Bucket)}
            options={[
              { value: 'overdue', label: t('money.overdue') },
              { value: 'due', label: t('money.dueTab') },
              { value: 'received', label: t('money.receivedTab') },
            ]}
          />
          {shown.length === 0 ? (
            <Card>
              <p className="k-t-body k-muted">{t('money.none')}</p>
            </Card>
          ) : (
            <List
              label={t(
                `money.${bucket === 'due' ? 'dueTab' : bucket === 'received' ? 'receivedTab' : 'overdue'}`,
              )}
            >
              {shown.map((r) => (
                <ListItem
                  key={r.id}
                  title={r.propertyTitle}
                  supporting={[
                    r.agentName,
                    r.branchName,
                    r.dueOn === null
                      ? null
                      : t('money.dueOn', { date: formatCalendarDate(r.dueOn) }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  trailing={
                    r.balancePaise > 0 ? (
                      <span className="money-trail">
                        <b className="k-t-label k-num">{formatRupees(r.balancePaise)}</b>
                      </span>
                    ) : (
                      <span className="k-t-label">{t('money.paid')}</span>
                    )
                  }
                  action={
                    r.balancePaise > 0 ? (
                      <Button disabled={chrome.readOnly} onClick={() => setSelected(r)}>
                        {t('receipt.record')}
                      </Button>
                    ) : undefined
                  }
                />
              ))}
            </List>
          )}
        </>
      ) : null}
      <ReceiptSheet
        dealId={selected?.id ?? null}
        balancePaise={selected?.balancePaise ?? 0}
        title={selected?.propertyTitle ?? ''}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </Screen>
  );
}
